/**
 * Bonded Matter PDP: client-side Metal / Size availability vs selected Carat (and Metal for sizes).
 * Mirrors Liquid maps in product-variant-picker.liquid. Vanilla JS only.
 */
(function () {
  'use strict';

  const PROP_PREFIXES = ['Jewelry material', 'Ring size'];

  let applyingInternal = false;

  /** One active ring-size pointer drag: window listeners are registered once to avoid listener leaks on section refresh. */
  const ringSizeDrag = { dragging: false, host: null, setByClientX: null };

  let ringSizeWindowPointerListenersBound = false;

  function variantSellsRegardlessOfStock(variant) {
    const fn = window.FoxTheme && window.FoxTheme.variantSellsRegardlessOfStock;
    if (typeof fn !== 'function') return false;
    try {
      return !!fn(variant);
    } catch {
      return false;
    }
  }

  function optVal(variant, pos) {
    if (!variant || !pos) return '';
    if (pos === 1) return variant.option1 || '';
    if (pos === 2) return variant.option2 || '';
    if (pos === 3) return variant.option3 || '';
    return '';
  }

  function parseVariantsJson(scriptEl) {
    if (!scriptEl || !scriptEl.textContent) return [];
    try {
      return JSON.parse(scriptEl.textContent.trim());
    } catch {
      return [];
    }
  }

  function getProductForm(productInfo, sectionId) {
    return (
      productInfo.querySelector(`#product-form-${sectionId}`) ||
      productInfo.querySelector('form[is="product-form"]') ||
      productInfo.querySelector('form.main-product-form')
    );
  }

  function clearBmLineItemProps(form) {
    if (!form) return;
    form.querySelectorAll('input[type="hidden"][name]').forEach((input) => {
      const n = input.getAttribute('name') || '';
      if (!n.startsWith('properties[')) return;
      const inner = n.slice('properties['.length, -1);
      if (PROP_PREFIXES.some((p) => inner.startsWith(p))) {
        input.value = '';
      }
    });
  }

  function readSelectedByPosition(variantSelects) {
    const byPos = {};
    variantSelects.querySelectorAll('[data-option-position]').forEach((group) => {
      const pos = parseInt(group.getAttribute('data-option-position'), 10);
      if (!pos || Number.isNaN(pos)) return;
      const radio = group.querySelector('input[type="radio"]:checked');
      if (radio) {
        byPos[pos] = radio.value;
        return;
      }
      const sel = group.querySelector('select');
      if (sel && sel.selectedOptions.length) {
        byPos[pos] = sel.selectedOptions[0].value || sel.value;
      }
    });
    return byPos;
  }

  function findSelectedVariant(variantSelects) {
    const variants = parseVariantsJson(variantSelects.querySelector('script.bm-pdp-variants-json[data-bm-variants]'));
    const selected = readSelectedByPosition(variantSelects);
    // A partial selection must never resolve to another combination's price or cart ID.
    return variants.find((variant) => [1, 2, 3].every((pos) => {
      const value = optVal(variant, pos);
      return value ? selected[pos] === value : !selected[pos];
    })) || null;
  }

  /** Resolve the exact DOM selection, not the stale section's data-selected-variant. */
  function resolveVariantIdFromDom(variantSelects) {
    const variant = findSelectedVariant(variantSelects);
    return variant?.id != null ? String(variant.id) : '';
  }

  /** Only a real, exact combination may supply a catalogue price. */
  function findDisplayVariant(productInfo) {
    const variantSelects = productInfo && productInfo.querySelector('variant-selects');
    return variantSelects ? findSelectedVariant(variantSelects) : null;
  }

  function updatePriceForProductInfo(productInfo, variant) {
    if (!productInfo || !window.FoxTheme?.Currency?.formatMoney) return;

    const sectionId = productInfo.dataset.section;
    const priceRoot = document.getElementById(`price-${sectionId}`);
    if (!priceRoot) return;

    const priceWrapper = priceRoot.querySelector('.f-price');
    if (!priceWrapper) return;

    const moneyFormat = FoxTheme.settings.moneyFormat;
    const price = variant ? variant.price : 0;
    const compareAt = variant?.compare_at_price;
    const onSale = compareAt && compareAt > price;
    const sells = !!variant && (variant.available || variantSellsRegardlessOfStock(variant));

    priceRoot.classList.remove('hidden');
    priceWrapper.classList.toggle('f-price--sold-out', !sells);
    priceWrapper.classList.toggle('f-price--on-sale', !!onSale);

    const formattedPrice = FoxTheme.Currency.formatMoney(price, moneyFormat);
    const formattedCompare = compareAt
      ? FoxTheme.Currency.formatMoney(compareAt, moneyFormat)
      : '';

    const regularItems = priceWrapper.querySelectorAll('.f-price__regular .f-price-item--regular');
    const salePriceEl = priceWrapper.querySelector('.f-price__sale .f-price-item--sale');
    const saleCompareEls = priceWrapper.querySelectorAll('.f-price__sale .f-price-item--regular');
    if (!variant) {
      // Clear hidden sale/unit-price remnants as well as the visible regular price.
      if (salePriceEl) salePriceEl.innerHTML = formattedPrice;
      saleCompareEls.forEach((el) => { el.innerHTML = ''; });
      priceWrapper.querySelectorAll('.f-price__unit').forEach((el) => el.classList.add('hidden'));
    }

    if (onSale) {
      if (salePriceEl) salePriceEl.innerHTML = formattedPrice;
      saleCompareEls.forEach((el) => {
        el.innerHTML = `<s>${formattedCompare}</s>`;
      });
    } else {
      regularItems.forEach((el) => {
        el.innerHTML = formattedPrice;
      });
    }
  }

  function showUnavailablePrice(productInfo) {
    if (!productInfo) return;
    productInfo.currentVariant = null;
    productInfo.updateVariantInputs?.(null);
    productInfo.setUnavailable?.();
    // No variant matches: clear stale stone details in every unavailable path.
    productInfo.querySelectorAll('[data-bm-mf-tcw], [data-bm-mf-dimension], [data-bm-stone-configuration]')
      .forEach((element) => { element.textContent = ''; });
    productInfo.querySelectorAll('[data-bm-mf-tcw-row], [data-bm-mf-dimension-row], [data-bm-stone-configuration]')
      .forEach((element) => { element.classList.add('hidden'); });
    ['Badges', 'PricePerItem', 'QuantityRules', 'VolumeNote'].forEach((prefix) => {
      productInfo.querySelector(`#${prefix}-${productInfo.dataset.section}`)?.classList.add('hidden');
    });
    // Prevent stale IDs from being submitted by main or installment forms.
    document.querySelectorAll(
      `#product-form-${productInfo.dataset.section} [name="id"], ` +
      `#product-form-installment-${productInfo.dataset.section} [name="id"]`
    ).forEach((input) => {
      input.value = '';
      input.setAttribute('disabled', 'disabled');
    });
    updatePriceForProductInfo(productInfo, null);
  }

  function syncDisplayPriceForPartialPick(productInfo) {
    const variantSelects = productInfo.querySelector('variant-selects');
    if (!variantSelects || variantSelects.getAttribute('data-bm-allow-unavailable-picks') !== 'true') return;
    const variants = parseVariantsJson(variantSelects.querySelector('script.bm-pdp-variants-json[data-bm-variants]'));
    if (!variants.length || findSelectedVariant(variantSelects)) return;
    showUnavailablePrice(productInfo);
  }

  /** Hide nonexistent shape/carat combinations, independently of stock and icon settings.
   * Return the corrected control so the caller can publish one coherent selection.
   */
  function syncShapeCarats(variantSelects) {
    if (!variantSelects) return null;
    const ctx = variantSelects.querySelector('[id^="bm-pdp-picker-context-"]');
    const shapePos = parseInt(ctx?.getAttribute('data-shape-pos'), 10) || 0;
    const caratPos = parseInt(ctx?.getAttribute('data-carat-pos'), 10) || 0;
    if (!shapePos || !caratPos) return null;
    const selected = readSelectedByPosition(variantSelects);
    if (!selected[shapePos]) return null;
    const variants = parseVariantsJson(variantSelects.querySelector('script.bm-pdp-variants-json[data-bm-variants]'));
    if (!variants.length) return null;
    const supported = new Set(variants.filter((variant) =>
      Object.entries(selected).every(([pos, value]) =>
        Number(pos) === caratPos || optVal(variant, Number(pos)) === value
      )
    ).map((variant) => optVal(variant, caratPos)));
    variantSelects.dataset.bmNoShapeCarats = supported.size ? 'false' : 'true';
    const group = variantSelects.querySelector(`[data-option-position="${caratPos}"]`);
    if (!group) return null;
    const select = group.querySelector('select');
    const controls = Array.from(group.querySelectorAll(select ? 'option' : 'input[type="radio"]'));
    controls.forEach((control) => {
      const valid = supported.has(control.value);
      control.disabled = !valid;
      control.hidden = !valid;
      control.classList.toggle('disabled', !valid);
      const label = Array.from(group.querySelectorAll('label')).find((el) => el.htmlFor === control.id);
      if (label) {
        label.hidden = !valid;
        // Theme swatch display rules must not override the hidden attribute.
        label.style.display = valid ? '' : 'none';
      }
    });
    if (supported.has(selected[caratPos])) return null;
    const first = controls.find((control) => !control.disabled);
    if (select) {
      controls.forEach((control) => {
        control.selected = control === first;
        control.toggleAttribute('selected', control === first);
      });
      if (!first) select.selectedIndex = -1;
    } else {
      controls.forEach((control) => { control.checked = control === first; });
    }
    return first ? select || first : null;
  }

  function computeAvailableMetals(variants, caratPos, caratVal, metalPos) {
    const set = new Set();
    if (!metalPos) return set;
    variants.forEach((v) => {
      if (!variantSellsRegardlessOfStock(v) && !v.available) return;
      if (caratPos && optVal(v, caratPos) !== caratVal) return;
      const m = optVal(v, metalPos);
      if (m) set.add(m);
    });
    return set;
  }

  function computeAvailableSizes(variants, caratPos, caratVal, metalPos, metalVal, sizePos) {
    const set = new Set();
    if (!sizePos) return set;
    variants.forEach((v) => {
      if (!variantSellsRegardlessOfStock(v) && !v.available) return;
      if (caratPos && optVal(v, caratPos) !== caratVal) return;
      if (metalPos && optVal(v, metalPos) !== metalVal) return;
      const s = optVal(v, sizePos);
      if (s) set.add(s);
    });
    return set;
  }

  function setGroupAvailability(fieldset, availableSet, form) {
    if (!fieldset) return false;
    const inputs = fieldset.querySelectorAll('input[type="radio"]');
    let hadInvalidSelection = false;

    inputs.forEach((input) => {
      const ok = availableSet.has(input.value);
      input.disabled = !ok;
      input.classList.toggle('disabled', !ok);
      if (ok) {
        input.removeAttribute('aria-disabled');
      } else {
        input.setAttribute('aria-disabled', 'true');
      }
      const label = Array.from(fieldset.querySelectorAll('label')).find((l) => l.htmlFor === input.id);
      if (label) {
        label.classList.toggle('bm-pdp-option--unavailable', !ok);
      }
      if (input.checked && !ok) {
        hadInvalidSelection = true;
        input.checked = false;
      }
    });

    if (hadInvalidSelection) {
      clearBmLineItemProps(form);
      const firstOk = Array.from(inputs).find((inp) => !inp.disabled);
      if (firstOk) {
        applyingInternal = true;
        firstOk.checked = true;
        firstOk.dispatchEvent(new Event('change', { bubbles: true }));
        queueMicrotask(() => {
          applyingInternal = false;
        });
      }
    }

    return hadInvalidSelection;
  }

  /** Strips script tags and event-handler attributes from metafield HTML before innerHTML (defence in depth). */
  function sanitizeStoneConfigurationHtml(html) {
    if (typeof html !== 'string' || !html.trim()) return '';
    try {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      doc.querySelectorAll('script').forEach((el) => el.remove());
      doc.querySelectorAll('*').forEach((el) => {
        for (let i = el.attributes.length - 1; i >= 0; i -= 1) {
          const name = el.attributes[i].name;
          if (/^on/i.test(name)) el.removeAttribute(name);
        }
      });
      return doc.body ? doc.body.innerHTML : '';
    } catch {
      return '';
    }
  }

  function syncVariantMetafieldSummary(productInfo) {
    if (!productInfo) return;
    const variantSelects = productInfo.querySelector('variant-selects');
    if (!variantSelects) return;

    const mfScript = variantSelects.querySelector('script[data-bm-variant-metafields]');
    if (!mfScript || !mfScript.textContent) return;
    let mf;
    try {
      mf = JSON.parse(mfScript.textContent.trim());
    } catch {
      return;
    }

    let variantId = resolveVariantIdFromDom(variantSelects);
    // Only use section JSON when there is no DOM catalogue to validate against.
    if (!variantId && !variantSelects.querySelector('script.bm-pdp-variants-json[data-bm-variants]')) {
      const selVarScript = variantSelects.querySelector('script[data-selected-variant]');
      if (selVarScript && selVarScript.textContent) {
        try {
          const v = JSON.parse(selVarScript.textContent.trim());
          if (v && v.id) variantId = String(v.id);
        } catch {
          /* ignore */
        }
      }
    }

    const tcwMap = (mf && mf.total_carat_weight) || {};
    const dimMap = (mf && mf.stone_dimensions) || {};
    const stoneMap = (mf && mf.stone_configuration) || {};

    const tcwRow = variantSelects.querySelector('[data-bm-mf-tcw-row]');
    const tcwEl = variantSelects.querySelector('[data-bm-mf-tcw]');
    if (tcwRow && tcwEl) {
      const tcw = tcwMap[variantId];
      const tcwStr = tcw == null ? '' : String(tcw).trim();
      if (!tcwStr) {
        tcwEl.textContent = '';
        tcwRow.classList.add('hidden');
      } else {
        tcwEl.textContent = tcwStr;
        tcwRow.classList.remove('hidden');
      }
    }

    const dimRow = variantSelects.querySelector('[data-bm-mf-dimension-row]');
    const dimEl = variantSelects.querySelector('[data-bm-mf-dimension]');
    if (dimRow && dimEl) {
      const dim = dimMap[variantId];
      const dimStr = dim == null ? '' : String(dim).trim();
      if (!dimStr) {
        dimEl.textContent = '';
        dimRow.classList.add('hidden');
      } else {
        dimEl.textContent = dimStr;
        dimRow.classList.remove('hidden');
      }
    }

    const target = variantSelects.querySelector('[data-bm-stone-configuration]');
    if (!target) return;

    const raw = stoneMap[variantId];
    if (raw == null) {
      target.textContent = '';
      target.classList.add('hidden');
      return;
    }
    if (typeof raw === 'string') {
      if (!raw.trim()) {
        target.textContent = '';
        target.classList.add('hidden');
        return;
      }
      const safe = sanitizeStoneConfigurationHtml(raw);
      target.innerHTML = safe;
    } else {
      target.textContent = String(raw);
    }
    target.classList.remove('hidden');
  }

  function initRingSizeSteps(root) {
    const scope = root || document;
    const hosts = scope.querySelectorAll('[data-ring-size-steps-host]');
    if (!hosts.length) return;

    hosts.forEach((host) => {
      const radios = Array.from(host.querySelectorAll('input[type="radio"][data-ring-size-step]'));
      const heading = host.querySelector('[data-ring-size-selected-heading]');
      const readout = host.querySelector('[data-ring-size-readout]');
      const readoutValue = host.querySelector('[data-ring-size-readout-value]');
      const track = host.querySelector('.bm-ring-size-track');
      const stepsLayer = host.querySelector('.bm-ring-size-steps');
      if (!radios.length) return;

      const maxIndex = Math.max(1, radios.length - 1);
      const updateSelection = (idx) => {
        let index = idx;
        if (Number.isNaN(index) || index < 0) index = 0;
        if (index > radios.length - 1) index = radios.length - 1;
        const selected = radios[index];
        if (!selected) return;
        if (!selected.checked) selected.checked = true;
        const value = String(selected.value || '').trim();
        const pct = maxIndex > 0 ? (index / maxIndex) * 100 : 0;
        host.style.setProperty('--bm-ring-selected-pct', `${pct}%`);
        if (heading) heading.textContent = value;
        if (readoutValue) readoutValue.textContent = value;
        if (readout) {
          readout.style.left = `${pct}%`;
          readout.classList.toggle('is-edge-start', pct <= 8);
          readout.classList.toggle('is-edge-end', pct >= 92);
        }
      };

      const alreadyBound = host.getAttribute('data-ring-size-slider-bound') === 'true';
      if (alreadyBound) {
        const checkedIdx = radios.findIndex((radio) => radio.checked);
        updateSelection(checkedIdx < 0 ? 0 : checkedIdx);
        return;
      }
      radios.forEach((radio, idx) => {
        radio.addEventListener('change', () => updateSelection(idx));
      });
      const setByClientX = (clientX) => {
        if (!track) return;
        const rect = track.getBoundingClientRect();
        if (!rect.width) return;
        const raw = (clientX - rect.left) / rect.width;
        const pct = Math.max(0, Math.min(1, raw));
        const idx = Math.round(pct * maxIndex);
        updateSelection(idx);
      };
      const canInteract = (target) => {
        if (!(target instanceof HTMLElement)) return false;
        return !!target.closest('.bm-ring-size-track, .bm-ring-size-steps, .bm-ring-size-step-hit');
      };
      if (track && stepsLayer) {
        if (!ringSizeWindowPointerListenersBound) {
          ringSizeWindowPointerListenersBound = true;
          const endRingSizeDrag = () => {
            if (!ringSizeDrag.dragging) return;
            if (ringSizeDrag.host) ringSizeDrag.host.classList.remove('is-dragging');
            ringSizeDrag.dragging = false;
            ringSizeDrag.host = null;
            ringSizeDrag.setByClientX = null;
          };
          window.addEventListener('pointermove', (event) => {
            if (!ringSizeDrag.dragging) return;
            if (typeof ringSizeDrag.setByClientX !== 'function') return;
            event.preventDefault();
            ringSizeDrag.setByClientX(event.clientX);
          });
          window.addEventListener('pointerup', endRingSizeDrag);
          window.addEventListener('pointercancel', endRingSizeDrag);
        }
        host.addEventListener('pointerdown', (event) => {
          if (!canInteract(event.target)) return;
          event.preventDefault();
          ringSizeDrag.dragging = true;
          ringSizeDrag.host = host;
          ringSizeDrag.setByClientX = setByClientX;
          host.classList.add('is-dragging');
          setByClientX(event.clientX);
        });
      }
      host.addEventListener('click', (event) => {
        if (!canInteract(event.target)) return;
        if (!track) return;
        if (typeof event.clientX !== 'number' || event.clientX <= 0) return;
        setByClientX(event.clientX);
      });
      const checkedIdx = radios.findIndex((radio) => radio.checked);
      updateSelection(checkedIdx < 0 ? 0 : checkedIdx);
      host.setAttribute('data-ring-size-slider-bound', 'true');
    });
  }

  function applyForProductInfo(productInfo) {
    if (applyingInternal) return;

    const variantSelects = productInfo.querySelector('variant-selects');
    if (!variantSelects) return;

    initRingSizeSteps(productInfo);
    const correctedCarat = syncShapeCarats(variantSelects);
    if (correctedCarat) {
      correctedCarat.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }
    if (variantSelects.dataset.bmNoShapeCarats === 'true') {
      showUnavailablePrice(productInfo);
      return;
    }
    syncVariantMetafieldSummary(productInfo);

    if (variantSelects.getAttribute('data-bm-allow-unavailable-picks') === 'true') {
      ['.bm-pdp-meta-picker--metal', '.bm-pdp-meta-picker--size'].forEach((sel) => {
        const fieldset = variantSelects.querySelector(sel);
        if (!fieldset) return;
        fieldset.querySelectorAll('input[type="radio"]').forEach((input) => {
          input.disabled = false;
          input.classList.remove('disabled');
          input.removeAttribute('aria-disabled');
        });
        fieldset.querySelectorAll('.bm-pdp-option--unavailable').forEach((el) => {
          el.classList.remove('bm-pdp-option--unavailable');
        });
      });
      syncDisplayPriceForPartialPick(productInfo);
      return;
    }

    const ctx = productInfo.querySelector('[id^="bm-pdp-picker-context-"]');
    if (!ctx) return;

    const scriptEl = variantSelects.querySelector('script.bm-pdp-variants-json[data-bm-variants]');
    const variants = parseVariantsJson(scriptEl);
    if (!variants.length) return;

    const sectionId = ctx.getAttribute('data-section-id') || '';
    const caratPos = parseInt(ctx.getAttribute('data-carat-pos'), 10) || 0;
    const metalPos = parseInt(ctx.getAttribute('data-metal-pos'), 10) || 0;
    const sizePos = parseInt(ctx.getAttribute('data-size-pos'), 10) || 0;
    const enableMetal = ctx.getAttribute('data-enable-metal') === 'true';
    const enableSize = ctx.getAttribute('data-enable-size') === 'true';

    const form = getProductForm(productInfo, sectionId);
    const selected = readSelectedByPosition(variantSelects);
    const caratVal = caratPos ? selected[caratPos] : '';
    const metalVal = metalPos ? selected[metalPos] : '';

    const metalFs = variantSelects.querySelector('.bm-pdp-meta-picker--metal');
    const sizeFs = variantSelects.querySelector('.bm-pdp-meta-picker--size');

    if (enableMetal && metalFs && metalPos) {
      const availM = computeAvailableMetals(variants, caratPos, caratVal, metalPos);
      const metalReset = setGroupAvailability(metalFs, availM, form);
      if (metalReset) return;
    }

    const selected2 = readSelectedByPosition(variantSelects);
    const metalVal2 = metalPos ? selected2[metalPos] : metalVal;
    const caratVal2 = caratPos ? selected2[caratPos] : caratVal;

    if (enableSize && sizeFs && sizePos) {
      const availS = computeAvailableSizes(variants, caratPos, caratVal2, metalPos, metalVal2, sizePos);
      setGroupAvailability(sizeFs, availS, form);
    }

    syncDisplayPriceForPartialPick(productInfo);
  }

  function applyAll() {
    initRingSizeSteps(document);
    document.querySelectorAll('product-info[id^="MainProduct"]').forEach((el) => {
      try {
        applyForProductInfo(el);
      } catch {
        /* Keep slider and other PDP scripts alive even if one product-info chunk breaks. */
      }
    });
  }

  /* Total Carat Weight + Metal info tooltips were previously click-toggled by `closeAllTcWPopovers`
     / `initTcWPopovers`. `origin/main` migrated those to CSS-only `[data-tooltip]:hover/:focus-within`
     using the theme's stock tooltip pattern (see `assets/bm-overrides.css` `.bm-pdp-tcw-tooltip` +
     `.bm-pdp-metal-info-tooltip` rules), so the JS handlers are no longer required. */

  function init() {
    applyAll();

    if (window.FoxTheme?.pubsub?.subscribe && window.FoxTheme?.pubsub?.PUB_SUB_EVENTS) {
      window.FoxTheme.pubsub.subscribe(window.FoxTheme.pubsub.PUB_SUB_EVENTS.optionValueSelectionChange, () => {
        window.requestAnimationFrame(() => applyAll());
      });
    }

    document.addEventListener('bm-hero:product-info-swapped', () => {
      window.requestAnimationFrame(() => applyAll());
    });
  }

  window.BmProductPickers = {
    syncShapeCarats,
    findDisplayVariant,
    updatePriceForProductInfo,
    showUnavailablePrice,
    syncDisplayPriceForPartialPick,
    resolveVariantIdFromDom,
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
